import { useState } from "react";

import { IconWarning, IconX } from "@btnet/blueprint-icons";
import { BTButton } from "@btnet/core-components/BTButton";
import { BTLink } from "@btnet/core-components/BTLink";
import { type IBTLinkRouting } from "@btnet/core-components/BTLink/link.types";
import { BTText } from "@btnet/core-components/BTText";

import { BTLocalStorage } from "types/btStorage";

import { isBrowserSupported } from "utilities/document/isBrowserSupported";
import { routes } from "utilities/routes";
import { formatRouteForNewTab } from "utilities/url/url";

import "./BrowserSupportedWarning.less";

const KEY = "bt-string-hideBrowserNotSupportedTime" as const;
const SHOW_EVERY = 4 * 60 * 60 * 1000; // 4 hours

// This banner renders in App.tsx as a sibling of AppProvider, outside every layout
// and therefore outside every BTLinkRoutingProvider, so BTLink would throw on the
// ambient lookup. Supply routing explicitly, the way SelectionOptionsList does for
// its detached toast root. `isSpa` is false because there is no SPA layout above us;
// nothing here reads it anyway - these are all external `href` links with no `to`.
const linkRouting: IBTLinkRouting = {
    isSpa: false,
    appBase: routes.appBase,
    formatRouteForNewTab,
};

export function BrowserSupportedWarning() {
    // will show if reloaded after SHOW_EVERY time has passed
    const [supported, setSupported] = useState<boolean>(() => {
        const dismissedAt: number = Date.parse(BTLocalStorage.get(KEY) || "0") + SHOW_EVERY;
        const now: number = Date.now();
        if (dismissedAt && dismissedAt > now) {
            return true;
        }
        return isBrowserSupported();
    });
    const handleDismiss = () => {
        BTLocalStorage.set(KEY, new Date().toISOString());
        setSupported(true);
    };
    if (!supported) {
        return (
            <div className="flex flex-col browser-supported-warning-container">
                <div className="flex flex-row browser-supported-warning">
                    <IconWarning />
                    <BTText tag="p" size="normal-md">
                        You are using an unsupported browser. For the best experience, please use
                        the latest version of&nbsp;
                        <BTLink
                            data-testid="chrome"
                            routing={linkRouting}
                            href="https://www.google.com/chrome/"
                            shouldOpenNewTab
                            className="px-4"
                        >
                            Google Chrome
                        </BTLink>
                        ,&nbsp;
                        <BTLink
                            data-testid="firefox"
                            routing={linkRouting}
                            href="https://www.mozilla.org/firefox/"
                            shouldOpenNewTab
                        >
                            Firefox
                        </BTLink>
                        ,&nbsp;
                        <BTLink
                            data-testid="edge"
                            routing={linkRouting}
                            href="https://www.microsoft.com/edge"
                            shouldOpenNewTab
                        >
                            Edge
                        </BTLink>
                        , or&nbsp;
                        <BTLink
                            data-testid="safari"
                            routing={linkRouting}
                            href="https://www.apple.com/safari/"
                            shouldOpenNewTab
                        >
                            Safari
                        </BTLink>
                        .
                    </BTText>
                    <BTButton
                        data-testid="close-browser-warning"
                        icon={<IconX />}
                        type="tertiary"
                        aria-label="Close"
                        onClick={() => handleDismiss()}
                    />
                </div>
            </div>
        );
    }
    return null;
}
